import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CMSConsumerCarouselBanner from '../CMS/components/banner/CMSConsumerCarouselBanner';
import TextHeader from './components/TextHeader';
import ImageCards from './components/ImageCards';
import CatalogueGrid from './components/CatalogueGrid';
import { captureAnalytics } from '../../utilities/Analytics';

// Import the combined API
import {
  apiGetInternationalsDisplay,
  InternationalSettings,
  InternationalBrandPage
} from '../../api/internationalsApiDisplay';

function InternationalsPage() {
  const location = useLocation();

  // State for API data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<InternationalSettings | null>(null);
  const [brands, setBrands] = useState<InternationalBrandPage[]>([]);

  // Load data from API
  useEffect(() => {
    const loadInternationalData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Loading international data...');
        const response = await apiGetInternationalsDisplay();

        if (response.success && response.settings) {
          console.log('API Response:', response);
          setSettings(response.settings);

          // Get brands from settings.brands
          const brandsData = response.settings.brands || [];
          console.log('Loaded brands:', brandsData);
          console.log('Published brands:', brandsData.filter(b => b.is_published));
          setBrands(brandsData);
        } else {
          console.error('API Error:', response.msg);
          setError(response.msg || 'Failed to load international data');
        }
      } catch (err) {
        console.error('Error loading international data:', err);
        setError('Failed to load international data');
      } finally {
        setLoading(false);
      }
    };

    loadInternationalData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    captureAnalytics('pageview', location.pathname, 'Internationals');
  }, [location.pathname]);

  // Helper functions to prepare data for components - USING CONSUMER DATA
  const getCarouselImages = () => {
    if (!settings || !settings.consumer_banner_carousel) return [];

    // Use consumer banner carousel if set to carousel mode
    if (settings.consumer_banner_media_switch === 'carousel') {
      console.log('Using API consumer carousel images:', settings.consumer_banner_carousel);
      return settings.consumer_banner_carousel.map(item => ({
        url: item.img_url || item.video_url || ''
      })).filter(item => item.url !== '');
    }

    return [];
  };

  const getCarouselVideo = () => {
    if (!settings || settings.consumer_banner_media_switch !== 'video') return undefined;

    if (settings.consumer_banner_video_url) {
      console.log('Using API consumer video:', settings.consumer_banner_video_url);
      return {
        video_url: settings.consumer_banner_video_url,
        thumbnail_url: '' // Add if you have thumbnails
      };
    }

    return undefined;
  };

  // Helper function to calculate aspect ratio from API dimensions - USING CONSUMER DATA
  const getCarouselAspectRatio = () => {
    if (!settings) return "56.25%"; // Default 16:9

    const width = settings.consumer_banner_width || 2800;
    const height = settings.consumer_banner_height || 1600;

    // Calculate aspect ratio as percentage for paddingTop
    const aspectRatio = (height / width) * 100;
    return `${aspectRatio}%`;
  };

  const transformBrandPageLink = (brandPageLink: string | null | undefined): string | null | undefined => {
    if (!brandPageLink || typeof brandPageLink !== 'string') {
      return brandPageLink;
    }

    // Check if the URL contains "trade." and remove it
    if (brandPageLink.includes('trade.')) {
      return brandPageLink.replace('trade.', '');
    }

    return brandPageLink;
  };

// Updated getBrandAssets function for InternationalsPage.tsx
  const getBrandAssets = () => {
    if (!brands || brands.length === 0) {
      console.log('No brands available');
      return [];
    }

    console.log('Processing brands:', brands);

    const publishedBrands = brands.filter(brand => {
      console.log(`Brand ${brand.page_title}: published=${brand.is_published}`);
      return brand.is_published === true;
    });

    console.log('Published brands after filter:', publishedBrands);

    const sortedBrands = publishedBrands.sort((a, b) => a.position_order - b.position_order);

    const brandAssets = sortedBrands.map(brand => {
      // Construct tile image URL from filename if needed
      const imageUrl = brand.tile_img_url ||
        (brand.tile_img ? `https://file-server.hertex-staging.net/media/large/${brand.tile_img}` : '');

      // Transform the brand_page_link to remove "trade." if present
      const transformedBrandPageLink = transformBrandPageLink(brand.brand_page_link);

      console.log(`Brand ${brand.page_title}: imageUrl=${imageUrl}, original_brand_page_link=${brand.brand_page_link}, transformed_brand_page_link=${transformedBrandPageLink}`);

      return {
        img: imageUrl,
        title: brand.page_title,
        url: `/internationals/brand/${brand.id}`,
        slug: brand.id.toString(),
        brand_page_link: transformedBrandPageLink // ✅ Use transformed URL
      };
    });

    console.log('Final brand assets:', brandAssets);
    return brandAssets;
  };

  const getCatalogueData = () => {
    if (!settings?.catalogue_section_items) return [];

    return settings.catalogue_section_items.map(item => ({
      id: item.id,
      img: item.catalogue_tile_img_url || '',
      url: item.issuu_link
    }));
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading international brands...
      </div>
    );
  }

  // Show error state only if there's an actual error AND we're not loading
  if (error && !loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        fontSize: '18px',
        color: '#d32f2f',
        textAlign: 'center'
      }}>
        <h2>Unable to load international brands</h2>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  // If we're not loading and have no settings, something went wrong
  if (!loading && !settings) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        fontSize: '18px',
        color: '#d32f2f',
        textAlign: 'center'
      }}>
        <h2>No international page data available</h2>
        <p>The international page may not be published yet.</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  const carouselImages = getCarouselImages();
  const carouselVideo = getCarouselVideo();
  const brandAssets = getBrandAssets();
  const catalogueData = getCatalogueData();
  const carouselAspectRatio = getCarouselAspectRatio();

  console.log('Final render data:', {
    carouselImages: carouselImages.length,
    carouselVideo: !!carouselVideo,
    brandAssets: brandAssets.length,
    catalogueData: catalogueData.length
  });

  return (
    <>
      {settings && (settings.consumer_banner_carousel.length > 0 || settings.consumer_banner_video) && (
        <CMSConsumerCarouselBanner
          bannerMediaOption={settings.consumer_banner_media_switch}
          bannerMediaCarousel={settings.consumer_banner_carousel.map(item => ({
            id: item.id,
            media_url: item.img_url || item.video_url || '',
            media_type: item.type,
            media_name: item.img_name || item.video_name || '' // ✅ Add this required field
          }))}
          bannerMediaVideo={settings.consumer_banner_video || null}
          bannerMediaVideoLink={settings.consumer_banner_video_url || ''}
          bannerLinkCategoryId={null}
          bannerWidth={settings.consumer_banner_width || 2800}
          bannerHeight={settings.consumer_banner_height || 1600}
          viewMode="live"
        />
      )}

      {/* Text Header - API data only */}
      {settings && (
        <TextHeader
          heading={settings.page_title}
          content={settings.page_description}
          isCentered={false}
          width='70%'
          marginTop={10}
          marginBottom={5}
        />
      )}

      {/* Image Cards - API brands only */}
      {brandAssets.length > 0 && (
        <ImageCards
          images={brandAssets}
          hasBackgroundText={true}
          useInternalLinks={true}
        />
      )}

      {/* Catalogue Grid - API catalogue data only */}
      {settings && catalogueData.length > 0 && (
        <CatalogueGrid
          header={settings.catalogue_section_heading || 'View our Latest Catalogues'}
          catalogueData={catalogueData}
        />
      )}

      {/* Show message if no content available */}
      {brandAssets.length === 0 && catalogueData.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#666'
        }}>
          <h3>No published brands or catalogues available</h3>
          <p>Please check back later for updated content.</p>
        </div>
      )}
    </>
  );
}

export default InternationalsPage;